import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../../environments/environment';

export interface ConceptStatusDto {
  statusMerchantConceptId: number;
  code: string;
  name: string;
  isActive: boolean;
  displayOrder: number;
}

export interface LocationConceptAdminDto {
  merchantLocationConceptId: string;
  merchantLocationId: string;
  conceptId?: string;
  title: string;
  description?: string;
  durationMinutes: number;
  price: number;
  bufferBeforeMinutes: number;
  bufferAfterMinutes: number;
  statusMerchantConceptId: number;
  statusName: string;
  staffCount: number;
  order: number;
}

export interface ReorderConceptItem {
  merchantLocationConceptId: string;
  order: number;
}

export interface ReorderLocationConceptsRequest {
  items: ReorderConceptItem[];
}

export interface ReorderLocationConceptsResponse {
  updatedCount: number;
}

export interface StaffAssignmentDto {
  employeeId: string;
  firstName: string;
  lastName: string;
  displayName: string;
  email?: string;
  phone?: string;
}

export interface CreateLocationConceptRequest {
  conceptId?: string;
  title: string;
  description?: string;
  durationMinutes: number;
  price: number;
  bufferBeforeMinutes: number;
  bufferAfterMinutes: number;
  statusMerchantConceptId: number;
  staffAssignments?: StaffAssignmentDto[];
}

export interface UpdateLocationConceptRequest {
  title: string;
  description?: string;
  durationMinutes: number;
  price: number;
  bufferBeforeMinutes: number;
  bufferAfterMinutes: number;
  statusMerchantConceptId: number;
  staffAssignments?: StaffAssignmentDto[];
}

export interface LocationConceptCreatedResponse {
  merchantLocationConceptId: string;
  merchantLocationId: string;
  conceptId?: string;
  title: string;
  staffAssignedCount: number;
}

export interface LocationConceptUpdatedResponse {
  merchantLocationConceptId: string;
  title: string;
  staffAssignedCount: number;
}

export interface ConceptStaffDto {
  merchantStaffId: string;
  employeeId?: string;
  firstName: string;
  lastName: string;
  displayName: string;
  email?: string;
  phone?: string;
}

export interface LocationConceptDetailResponse {
  merchantLocationConceptId: string;
  merchantLocationId: string;
  conceptId?: string;
  title: string;
  description?: string;
  durationMinutes: number;
  price: number;
  bufferBeforeMinutes: number;
  bufferAfterMinutes: number;
  statusMerchantConceptId: number;
  assignedStaff: ConceptStaffDto[];
}

export interface LocationConceptsAdminResponse {
  concepts: LocationConceptAdminDto[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface ConceptToCreateDto {
  conceptId?: string;
  title: string;
  description?: string;
  durationMinutes: number;
  price: number;
  bufferBeforeMinutes: number;
  bufferAfterMinutes: number;
  statusMerchantConceptId: number;
  staffAssignments?: StaffAssignmentDto[];
}

export interface BulkCreateLocationConceptsRequest {
  concepts: ConceptToCreateDto[];
}

export interface BulkCreateLocationConceptsResponse {
  successCount: number;
  failedCount: number;
  createdConcepts: Array<{
    merchantLocationConceptId: string;
    title: string;
    conceptId?: string;
  }>;
  errors: Array<{
    title: string;
    conceptId?: string;
    errorMessage: string;
  }>;
}

export interface SingleResponse<T> {
  isSuccess: boolean;
  value?: T;
  error?: {
    statusCode: number;
    messages: string[];
  };
}

export interface PaginableResponse<T> {
  isSuccess: boolean;
  values?: T[];
  total?: number;
  error?: {
    statusCode: number;
    messages: string[];
  };
}

@Injectable({
  providedIn: 'root'
})
export class LocationConceptService {
  private apiUrl = `${environment.apiUrl}/api/v2/booking/concepts`;

  constructor(private http: HttpClient) {}

  getConceptStatuses(): Observable<PaginableResponse<ConceptStatusDto>> {
    return this.http.get<PaginableResponse<ConceptStatusDto>>(`${this.apiUrl}/statuses`);
  }

  searchLocationConcepts(
    locationId: string,
    searchTerm?: string,
    statusId?: number,
    page: number = 1,
    pageSize: number = 20
  ): Observable<SingleResponse<LocationConceptsAdminResponse>> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());

    if (searchTerm) {
      params = params.set('search', searchTerm);
    }

    if (statusId !== undefined && statusId !== null) {
      params = params.set('statusId', statusId.toString());
    }

    return this.http.get<SingleResponse<LocationConceptsAdminResponse>>(
      `${this.apiUrl}/locations/${locationId}`,
      { params }
    );
  }

  getLocationConceptDetail(
    locationId: string,
    conceptId: string
  ): Observable<SingleResponse<LocationConceptDetailResponse>> {
    return this.http.get<SingleResponse<LocationConceptDetailResponse>>(
      `${this.apiUrl}/locations/${locationId}/concepts/${conceptId}`
    );
  }

  createLocationConcept(
    locationId: string,
    request: CreateLocationConceptRequest
  ): Observable<SingleResponse<LocationConceptCreatedResponse>> {
    return this.http.post<SingleResponse<LocationConceptCreatedResponse>>(
      `${this.apiUrl}/locations/${locationId}`,
      request
    );
  }

  updateLocationConcept(
    locationId: string,
    conceptId: string,
    request: UpdateLocationConceptRequest
  ): Observable<SingleResponse<LocationConceptUpdatedResponse>> {
    return this.http.put<SingleResponse<LocationConceptUpdatedResponse>>(
      `${this.apiUrl}/locations/${locationId}/concepts/${conceptId}`,
      request
    );
  }

  deleteLocationConcept(
    locationId: string,
    conceptId: string
  ): Observable<SingleResponse<DeletedResponse>> {
    return this.http.delete<SingleResponse<DeletedResponse>>(
      `${this.apiUrl}/locations/${locationId}/concepts/${conceptId}`
    );
  }

  bulkCreateLocationConcepts(
    locationId: string,
    request: BulkCreateLocationConceptsRequest
  ): Observable<SingleResponse<BulkCreateLocationConceptsResponse>> {
    return this.http.post<SingleResponse<BulkCreateLocationConceptsResponse>>(
      `${this.apiUrl}/locations/${locationId}/bulk`,
      request
    );
  }

  reorderLocationConcepts(
    locationId: string,
    items: ReorderConceptItem[]
  ): Observable<SingleResponse<ReorderLocationConceptsResponse>> {
    return this.http.put<SingleResponse<ReorderLocationConceptsResponse>>(
      `${this.apiUrl}/locations/${locationId}/reorder`,
      { items }
    );
  }
}

export interface DeletedResponse {
  message: string;
}
